/**
 * Date utilities for consistent date parsing across the application.
 * 
 * The main problem this solves: JavaScript's `new Date("YYYY-MM-DD")` interprets
 * date-only strings as UTC midnight, which causes dates to appear shifted by one day
 * in timezones with negative UTC offsets (e.g., Mexico UTC-6).
 * 
 * Solution: Parse date-only strings using component-based construction which
 * creates dates in local time without UTC conversion.
 */

/**
 * Parses a date string as a local date, avoiding UTC conversion issues.
 * 
 * For date-only strings (YYYY-MM-DD format), this creates a Date object
 * at local midnight instead of UTC midnight, preventing the off-by-one-day
 * bug in negative UTC offset timezones.
 * 
 * For strings that include time information (contain 'T'), falls back to
 * standard Date parsing to preserve the intended behavior.
 * 
 * @param dateStr - Date string in YYYY-MM-DD format or ISO format with time
 * @returns Date object interpreted in local time
 * 
 * @example
 * // In UTC-6 timezone:
 * // Before: new Date("2025-01-30") → Jan 29, 2025 18:00 (shows as Jan 29)
 * // After:  parseLocalDate("2025-01-30") → Jan 30, 2025 00:00 (shows as Jan 30)
 */
export function parseLocalDate(dateStr: string): Date {
  // If the string is empty or undefined, return current date
  if (!dateStr) {
    return new Date();
  }

  // Check if it's a date-only format (YYYY-MM-DD)
  // If it contains 'T', it's a full ISO timestamp and should use default parsing
  if (dateStr.includes('T')) {
    return new Date(dateStr);
  }

  // Check for YYYY-MM-DD format
  const dateOnlyMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  
  if (dateOnlyMatch) {
    const year = parseInt(dateOnlyMatch[1], 10);
    const month = parseInt(dateOnlyMatch[2], 10) - 1; // JavaScript months are 0-indexed
    const day = parseInt(dateOnlyMatch[3], 10);
    
    // Create date using component constructor (local time, not UTC)
    return new Date(year, month, day);
  }

  // Fallback for other formats
  return new Date(dateStr);
}

/**
 * Formats a date-only string for display, ensuring correct local date interpretation.
 * 
 * @param dateStr - Date string in YYYY-MM-DD format
 * @param options - Intl.DateTimeFormat options
 * @param locale - Locale string (default: 'es-ES')
 * @returns Formatted date string
 */
export function formatLocalDate(
  dateStr: string, 
  options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' },
  locale: string = 'es-ES'
): string {
  const date = parseLocalDate(dateStr);
  return date.toLocaleDateString(locale, options);
}
